import React, { useRef, useState, useEffect } from 'react';
import { View } from "react-native";
import { 
    Editor, 
    EditorState, 
    RichUtils, 
    CompositeDecorator, 
    Modifier, 
    ContentBlock, 
    SelectionState, 
    AtomicBlockUtils,
    convertToRaw,
    convertFromRaw,
    ContentState,
    DraftEntityMutability
} from 'draft-js';

import { Text, TouchableOpacity } from '../../../../globalComponents/NativeOverrides';
import { rti_findPlayerEntities, rti_findTagEntities } from './RTIHelpers';
import { rti_playerEntity, rti_tagEntity } from './RTIEntities';
import PlayerSelector from '../PlayerSelector';
import { RawDraftJSProps } from '../../../../store/types/soc_svc';

type RichTextInputProps = {
    formatted_body?: RawDraftJSProps,
    placeholder?:string,
    clear?: boolean,
    onClear?: () => void
    onUpdate:(formatted_body:RawDraftJSProps, body:string) => void
}

const RichTextInput = ({ formatted_body, placeholder, clear, onClear, onUpdate }:RichTextInputProps) => {
    const [ mention_active, setMentionActive ] = useState(false);
    const [ draft_loaded, setDraftLoaded ] = useState(false);
    const [ mention_value, setMentionValue ] = useState('')
    const [ tag_active, setTagActive ] = useState(false);
    const [ tag_value, setTagValue ] = useState('');
    const [ taggableSelection, setTaggableSelection ] = useState<SelectionState | undefined>()
    

    useEffect(() => {
        if(!formatted_body || draft_loaded){ return }
        let draftJSBody = convertFromRaw(formatted_body)
        setEditorState(EditorState.createWithContent(draftJSBody, decorator))
        return setDraftLoaded(true)
    },[formatted_body])
    
    useEffect(() => {
        if(!clear){ return }
        setEditorState(EditorState.createEmpty(decorator))
        if(onClear){ onClear() }
        return
    },[clear])

    const decorator = new CompositeDecorator([
        {
            strategy: rti_findPlayerEntities,
            component: rti_playerEntity,
            props: { test: 'hey!' }
        },
        {
            strategy: rti_findTagEntities,
            component: rti_tagEntity,
            props: { test: 'hey!' }
        }
    ]);

    const [ editor_state, setEditorState ] = useState(
        () => EditorState.createEmpty(decorator),
      );
      
    const editor_ref = useRef()

    const addSpaceToEditorAndFocus = (localEditor:EditorState) => {
        const selectionState = localEditor.getSelection()
        const contentState = localEditor.getCurrentContent();
        const contentStateWithEntity = contentState.createEntity('unstyled', 'MUTABLE')
        const contentStateWithTrail = Modifier.insertText(contentState, selectionState, ' ', undefined, contentStateWithEntity.getLastCreatedEntityKey())
        const newEditorState = EditorState.moveFocusToEnd(EditorState.set(localEditor, {
          currentContent: contentStateWithTrail
        }))
        return newEditorState
      }

    const checkBlock = (newState:EditorState) => {
        const contentState = newState.getCurrentContent()
        const lastBlock = contentState.getLastBlock()
        let slicedText = lastBlock.getText()
        if(!slicedText || slicedText === ''){ return }
        const selectionState = newState.getSelection()
        let end = selectionState.getEndOffset()
        slicedText = slicedText.slice(0, end)
        let start = slicedText.lastIndexOf(" "); //Gets the position of the first character in the new word
        if (start === -1) { start = 0; } else { start += 1 }
        const word = slicedText.slice(start); //This is the current word that is being typed (between spaces)
        
        if(word === '' && mention_active){ setMentionActive(false); setMentionValue('') }
        if(word === '' && tag_active && tag_value === ''){ setTagActive(false) }
        if(word === '' && tag_active && tag_value !== ''){ 
            setTimeout(() => { insertTag({ type: 'TAG', mutability:'IMMUTABLE', data: tag_value }) }, 0);  
        }
        if(word.charAt(0) === '@' && !mention_active){ setMentionActive(true)}
        if(word.charAt(0) === '#' && !tag_active){ setTagActive(true); }
        if(mention_active || tag_active){
            setTaggableSelection(new SelectionState({
              anchorKey: selectionState.getAnchorKey(),
              anchorOffset: start,
              focusKey: selectionState.getFocusKey(),
              focusOffset: start + word.length
            }))
        }
        if(word.charAt(0) !== '@' && mention_active){ setMentionActive(false) }
        if(mention_active){ setMentionValue(word.substring(1))}
        if(tag_active){ setTagValue(word.substring(1)) }
    }

    /*
    const checkForInsert = () => {
        if(taggableSelection && mention_active && mention_value !== ''){
            insertPlayer({type: 'PLAYER', mutability:'IMMUTABLE', data: mention_value})
        }
        setMentionActive(false)
    }
    */

    const insertPlayer = (entity:{ type:string, mutability:DraftEntityMutability, data:{ player_id:string, username:string } }) => {
        if(!taggableSelection){ return }
        const contentState = editor_state.getCurrentContent();
        const contentStateWithEntity = contentState.createEntity(entity.type, entity.mutability, entity.data)
        const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
        const contentStateWithTag = Modifier.replaceText(contentStateWithEntity, taggableSelection, `${entity.data.username}`, undefined, entityKey)
        const newEditorState = EditorState.push(editor_state, contentStateWithTag, 'insert-fragment')
        const finalEditorState = addSpaceToEditorAndFocus(newEditorState)
        setEditorState(finalEditorState)
        setTaggableSelection(undefined)
        setMentionActive(false)
        setMentionValue('')
      }

      const insertTag = (entity:{ type:string, mutability:DraftEntityMutability, data: string }) => {
        if(!taggableSelection){ return }
        const contentState = editor_state.getCurrentContent();
        const contentStateWithEntity = contentState.createEntity(entity.type, entity.mutability, entity.data)
        const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
        const contentStateWithTag = Modifier.replaceText(contentStateWithEntity, taggableSelection, `#${entity.data}`, undefined, entityKey)
        const newEditorState = EditorState.push(editor_state, contentStateWithTag, 'insert-fragment')
        const finalEditorState = addSpaceToEditorAndFocus(newEditorState)
        setEditorState(finalEditorState)
        setTaggableSelection(undefined)
        setTagActive(false)
        setTagValue('')
      }

    const onChange = (newState:EditorState) => {
        checkBlock(newState)
        //onChange(convertToRaw(newState.getCurrentContent()))
        setEditorState(newState)
        if(!onUpdate){ return }
        let rawData = convertToRaw(newState.getCurrentContent())
        let body = newState.getCurrentContent().getPlainText('\u0001')
        onUpdate(rawData, body)

    }

    const handleKeyCommand = (command:string, editorState:EditorState) => {
        const newState = RichUtils.handleKeyCommand(editorState, command);
        if (newState) {
          onChange(newState)
          return 'handled';
        }
        return 'not-handled';
      }

    return (
        <View style={{ flex:1 }}>
            <Editor
                ref={editor_ref}
                handleKeyCommand={handleKeyCommand}
                //handleBeforeInput={handleInput}
                placeholder={placeholder??'Start writing here, add gifs / images, tag your positions, @people, $competitions, #tag'}
                editorState={editor_state} 
                onChange={onChange} 
                />
            {mention_active ?
                <PlayerSelector player_id={'0'} max_height={250} search_value_override={mention_value??''} onPlayerSelect={(player) => insertPlayer({ type: 'PLAYER', mutability:'IMMUTABLE', data: { player_id:player.player_id, username:player.username } })}/>
            :<></>}
            {tag_active ?
            <TouchableOpacity onPress={() => insertTag({ type: 'TAG', mutability:'IMMUTABLE', data: tag_value })}>
                <Text>{tag_value}</Text>
            </TouchableOpacity>
            :<></>}
        </View>
    )
}


export default RichTextInput